import React from 'react'
import { useAuth } from '../auth/AuthProvider'
import { store, streak } from '../lib/store'
import { fetchRecentSessions } from '../lib/remoteSessions'
import { useToast } from '../components/Toast'
import { getDailyGoal } from '../lib/goals'
import { dailyMotivation } from '../lib/motivation'
import { IconBell, IconPersonMale, IconPersonFemale, IconUser } from '../components/icons'
import { currentLevel, totalXP } from '../lib/xp'
import { getProfile } from '../lib/profile'

export function Dashboard({ onOpenActivity, unread = 0, onNotifications }: { onOpenActivity?: () => void; unread?: number; onNotifications?: () => void }) {
  const { push } = useToast()
  const { text: motivation, author } = dailyMotivation()
  const [sessions, setSessions] = React.useState(store.all())
  const [remoteSessions, setRemoteSessions] = React.useState<typeof sessions>([])
  const { session } = useAuth()
  // Online/offline hint removed for a cleaner hero
  const s = streak()
  // PR streak not shown on the compact dashboard
  // const prStreak = getPrStreak()
  const allCombined = React.useMemo(() => {
    // Combine by id to avoid duplicates if the same session exists locally and remotely
    const map = new Map<string, typeof sessions[number]>()
    ;[...remoteSessions, ...sessions].forEach(s => { map.set(s.id, s) })
    return Array.from(map.values())
  }, [sessions, remoteSessions])
  const todayReps = allCombined.filter(x => new Date(x.startedAt).toDateString() === new Date().toDateString()).reduce((a, b) => a + b.reps, 0)
  const [goal, setGoal] = React.useState<number | null>(null)
  const pct = goal && goal > 0 ? Math.min(100, Math.round((todayReps / goal) * 100)) : 0
  const [displayName, setDisplayName] = React.useState<string>('')
  const [genderChar, setGenderChar] = React.useState<string>('🧑')
  const [avatarOverride] = React.useState<string>(()=>{ try { return localStorage.getItem('pt_avatar') || '💪' } catch { return '💪' } })
  // Promo image (PNG) fallback handling: show gradient orb until image loads
  const [promoImgOk, setPromoImgOk] = React.useState(false)
  const promoUrl = React.useMemo(() => {
    try {
      return new URL('../../motivationaltransparent.webp', import.meta.url).toString()
    } catch {
      return ''
    }
  }, [])
  // Simple 7-day summary count to surface an entry point to Activity
  const last7 = React.useMemo(() => {
    const end = new Date()
    const endOfDay = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999).getTime()
    const startTs = endOfDay - 6*86400000
    const by: Record<string, number> = {}
    for (let i = 0; i < 7; i++) {
      const ts = startTs + i*86400000
      const d = new Date(ts)
      const key = `${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,'0')}-${d.getDate().toString().padStart(2,'0')}`
      by[key] = 0
    }
    allCombined.filter(s => s.startedAt >= startTs && s.startedAt <= endOfDay).forEach(s => {
      const d = new Date(s.startedAt)
      const key = `${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,'0')}-${d.getDate().toString().padStart(2,'0')}`
      by[key] = (by[key]||0) + s.reps
    })
    const keys = Object.keys(by).sort()
    const values = keys.map(k => by[k]||0)
    const total = values.reduce((a,b)=>a+b,0)
    const max = Math.max(1, ...values)
    return { keys, values, total, max }
  }, [allCombined])

  // Level and XP (lightweight to compute each render)
  const { level } = currentLevel()
  const xpTotal = totalXP()

  // Share button removed for a cleaner dashboard

  React.useEffect(() => {
    const i = setInterval(() => setSessions(store.all()), 1000)
    return () => clearInterval(i)
  }, [push])

  // Load profile for personalized greeting and gender character
  React.useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        const p = await getProfile()
        if (!mounted) return
        // Prefer nickname, then first name, then metadata, then fallback
        const metaFirst = session?.user?.user_metadata?.first_name as string | undefined
        const name = (p?.nickname || p?.first_name || metaFirst || '').toString()
        setDisplayName(name)
        const g = p?.gender || 'N'
        setGenderChar(g === 'F' ? '👩' : g === 'M' ? '👨' : '🧑')
      } catch {
        // fallback to metadata/local storage if needed
        try {
          const localName = localStorage.getItem('pt_name') || ''
          setDisplayName(localName)
        } catch { /* ignore */ }
      }
    })()
    return () => { mounted = false }
  }, [session?.user?.id, session?.user?.user_metadata?.first_name])

  // Load remote sessions (last 30 days) once and on auth changes
  React.useEffect(() => {
    let mounted = true
    ;(async () => {
      try { const rows = await fetchRecentSessions(30); if (mounted) setRemoteSessions(rows) } catch { /* ignore */ }
    })()
    return () => { mounted = false }
  }, [session?.user?.id])

  // Online status listeners removed

  React.useEffect(() => {
    let mounted = true
    getDailyGoal().then(v => { if (mounted) setGoal(v) }).catch(() => { /* ignore */ })
    return () => { mounted = false }
  }, [])

  // Daily goal control moved to Profile; keep goal only for Today/goal display if set

  // Refresh remote sessions periodically and on window focus (cross-device updates)
  React.useEffect(() => {
    let mounted = true
    const refresh = async () => {
      try {
        const rows = await fetchRecentSessions(30)
        if (mounted) {
          setRemoteSessions(rows)
          // One-time toast to confirm cloud fetch arrived
          try {
            const k = 'pt_remote_fetch_toast'
            if (!sessionStorage.getItem(k)) { push(`Cloud sessions: ${rows.length}`); sessionStorage.setItem(k,'1') }
          } catch { /* ignore */ }
        }
      } catch { /* ignore */ }
    }
      const id = setInterval(refresh, 30000)
      const onFocus = () => { refresh() }
      const onGlobal = () => { refresh() }
      window.addEventListener('focus', onFocus)
      window.addEventListener('pt_cloud_refresh' as any, onGlobal)
    // Initial refresh shortly after mount
    const t = setTimeout(refresh, 1200)
      return () => { mounted = false; clearInterval(id); clearTimeout(t); window.removeEventListener('focus', onFocus); window.removeEventListener('pt_cloud_refresh' as any, onGlobal) }
  }, [push])

  const dateStr = React.useMemo(() => {
    try {
      return new Intl.DateTimeFormat(undefined, { weekday: 'short', day: '2-digit', month: 'long' }).format(new Date()).replace(',', '')
    } catch {
      return new Date().toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'long' }).replace(',', '')
    }
  }, [])

  return (
    <div className="container">
      {/* Welcome card */}
      <div className="list" style={{marginTop:0}}>
        <div className="welcome-card" style={{display:'grid', gridTemplateColumns:'auto 1fr auto', gap:12, alignItems:'center'}}>
          <div style={{fontSize:28, display:'grid', placeItems:'center', width:32, height:32}}>
            {avatarOverride !== '💪' ? (
              <span aria-hidden>{avatarOverride}</span>
            ) : (
              genderChar === '👩' ? <IconPersonFemale size={28} /> : genderChar === '👨' ? <IconPersonMale size={28} /> : <IconUser size={24} />
            )}
          </div>
          <div>
            <div className="welcome-date">{dateStr}</div>
            <div className="welcome-title">Hello{ displayName ? `, ${displayName}` : ''} 👋</div>
          </div>
          <div style={{display:'grid', gridAutoFlow:'column', alignItems:'center', gap:8}}>
            {unread>0 && <span className="notif-badge" aria-label={`${unread} unread`}>{unread}</span>}
            <button aria-label="Notifications" className="bell-btn" title="Notifications" onClick={()=> onNotifications && onNotifications()}>
              <IconBell />
            </button>
          </div>
        </div>
      </div>

      {/* Quote of the day promo card */}
      <div className="list">
        <div className="card promo-card">
          <div>
            <div className="card-title">Daily motivation</div>
            <div>“{motivation}”</div>
            <div className="card-sub" style={{marginTop:4}}>— {author}</div>
          </div>
          <div className="promo-art" aria-hidden>
            {!promoImgOk && <div className="promo-orb" />}
            {promoUrl && (
              <img
                src={promoUrl}
                alt=""
                className="promo-img"
                onLoad={()=>setPromoImgOk(true)}
                style={{display: promoImgOk ? 'block':'none'}}
              />
            )}
          </div>
        </div>
      </div>
      <div className="list">
        <div className="card ring-card" style={{width:'100%'}}>
          <div style={{display:'grid', gridTemplateColumns:'1fr auto 1fr', alignItems:'center', gap:12, width:'100%'}}>
            {/* Left: Level */}
            <div style={{justifySelf:'start', display:'grid', gap:2}}>
              <div className="card-sub" style={{fontSize:12}}>Level</div>
              <div style={{fontWeight:900, fontSize:18}}>L{level}</div>
            </div>
            {/* Center: Ring */}
            <div className="ring-meter" style={{position:'relative', justifySelf:'center'}}>
              {/* Fill arc in green up to the goal */}
              <div className="ring-bg" style={{['--pct' as any]: `${pct}%`}} />
              <div className="ring" />
              <div className="ring-inner">
                <div style={{display:'grid', placeItems:'center'}}>
                  <div style={{fontWeight:900, fontSize:24, lineHeight:1}}>{todayReps}</div>
                  <div style={{opacity:.8, fontSize:12}}>{goal && goal>0 ? `/ ${goal}` : 'today'}</div>
                </div>
              </div>
            </div>
            {/* Right: XP */}
            <div style={{justifySelf:'end', textAlign:'right', display:'grid', gap:2}}>
              <div className="card-sub" style={{fontSize:12}}>XP</div>
              <div style={{fontWeight:900, fontSize:18}}>{xpTotal}</div>
            </div>
          </div>
        </div>
      </div>


      <div className="kpi-grid">
        <div className="kpi-card"><div className="label">Today</div><div className="value">{todayReps}{goal && goal>0 ? ` / ${goal}` : ''}</div></div>
        <div className="kpi-card"><div className="label">Streak</div><div className="value">{s}d</div></div>
        <div className="kpi-card"><div className="label">Total</div><div className="value">{allCombined.reduce((a,b)=>a+b.reps,0)}</div></div>
      </div>

      <div className="list" style={{marginTop:10}}>
        <div className="card bars-card">
          <div className="bars-header">
            <div className="card-sub">Last 7 days</div>
            <button className="btn btn-ghost btn-chip" onClick={()=> onOpenActivity && onOpenActivity()} aria-label="Open activity details">View</button>
          </div>
          <div className="bars-grid">
            {last7.keys.map((k,i)=>{
              const v = last7.values[i]
              const h = Math.max(4, Math.round((v / last7.max) * 56))
              return (
                <div key={k} style={{display:'grid', gridTemplateRows:'1fr auto', gap:4, justifyItems:'center'}}>
                  <div className="bar" style={{height:h}} />
                  <div className="bar-label">{k.slice(5)}</div>
                </div>
              )
            })}
          </div>
          <div style={{display:'flex', justifyContent:'space-between', fontSize:12, opacity:.8}}>
            <span>Total</span>
            <b style={{opacity:0.9}}>{last7.total}</b>
          </div>
        </div>
      </div>

      <div className="list">
        <div className="card session-card">
          <div className="card-sub">Recent sessions</div>
          {allCombined.sort((a,b)=> (b.startedAt||0)-(a.startedAt||0)).slice(0,3).map(ss => (
            <div key={ss.id} className="session-row">
              <div style={{fontSize:12, opacity:.85}}>{new Date(ss.startedAt).toLocaleString()}</div>
              <div className="reps-pill">{ss.reps}</div>
            </div>
          ))}
          {allCombined.length===0 && <div className="session-row"><div>No sessions yet. Tap Start!</div></div>}
        </div>
      </div>
    </div>
  )
}
